import { useEffect, useId, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocation, useSearch } from "wouter";
import { Archive, ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { z } from "zod";
import { Alert, Button, ErrorState, PageHeader, Panel, SelectField, Skeleton, TextArea, TextField, cx } from "../components/ui.tsx";
import { AxisTag, StatusPill } from "../components/status.tsx";
import { GuardedLink } from "../components/plan-shell.tsx";
import { useToast } from "../components/toast.tsx";
import { ApiError, applyFieldErrors, get, post, put } from "../lib/api.ts";
import { planKey, usePlan } from "../lib/plan.tsx";
import { useUnsavedGuard } from "../lib/guard.tsx";
import type { InitiativeDetail, StructureAxis, Unit } from "../lib/types.ts";
import { actionSchema, derivedDates, submissionReadiness } from "../../shared/planning.ts";
import { formatDateBR, formatInstantBR } from "../../shared/dates.ts";
import { initiativeLabel, krLabel, objectiveLabel } from "../../shared/numbering.ts";
import { optionalText, requiredText } from "../../shared/setup.ts";
import { LIMITS } from "../../shared/limits.ts";
import { InitiativeActions } from "./InitiativeActions.tsx";
import { BackLink, OverflowMenu, StickyActions } from "../components/nav-controls.tsx";
import { ProgressPanel } from "../components/progress.tsx";
import { ReopenBanner } from "../components/reopen.tsx";
import { ArchiveDialog } from "./PlanningPage.tsx";
import { usePageTitle } from "../lib/nav.tsx";

const formSchema = z.object({
  title: requiredText(LIMITS.title, "Informe o título da iniciativa."),
  description: optionalText(LIMITS.longDescription),
  unit_id: z.string(),
  actions: z.array(actionSchema).max(LIMITS.collection),
});
export type InitiativeForm = z.input<typeof formSchema>;

const toForm = (d: InitiativeDetail | null): InitiativeForm => ({
  title: d?.title ?? "",
  description: d?.description ?? "",
  unit_id: d?.unit?.id ?? "",
  actions: (d?.actions ?? []).map((a) => ({ id: a.id, description: a.description ?? "", starts_on: a.starts_on ?? "", ends_on: a.ends_on ?? "", weight: a.weight })),
});

export const blankAction = () => ({ id: null, description: "", starts_on: "", ends_on: "", weight: null });

/** T07 route: an existing initiative, or a new draft under ?kr=<key result>. */
export function InitiativePage({ id }: { id: string }) {
  const plan = usePlan();
  const isNew = id === "nova";
  const krId = new URLSearchParams(useSearch()).get("kr");
  const detail = useQuery({
    queryKey: planKey(plan.id, "initiative", id),
    queryFn: async () => (await get<{ data: InitiativeDetail }>(`/plans/${plan.id}/initiatives/${id}`)).data,
    enabled: !isNew,
  });
  const structure = useQuery({
    queryKey: planKey(plan.id, "structure"),
    queryFn: async () => (await get<{ data: StructureAxis[] }>(`/plans/${plan.id}/structure`)).data,
    enabled: isNew,
  });

  if (isNew) {
    if (structure.isPending) return <Skeleton lines={6} />;
    if (structure.isError) return <ErrorState error={structure.error} onRetry={() => void structure.refetch()} />;
    for (const axis of structure.data) for (const o of axis.objectives) for (const k of o.key_results) {
      if (k.id === krId) {
        return (
          <InitiativeEditor
            key="new"
            detail={null}
            context={{ axis: { id: axis.id, name: axis.name, color: axis.color, editor_group_id: axis.editor_group_id, editor_group_name: axis.editor_group_name, number: axis.number }, objective: o, key_result: k }}
            canEdit={axis.can_edit}
          />
        );
      }
    }
    return <Alert tone="warn" title="KR não encontrado">Volte ao planejamento e escolha o KR em que deseja criar a iniciativa.</Alert>;
  }
  if (detail.isPending) return <Skeleton lines={8} />;
  if (detail.isError) {
    if (detail.error instanceof ApiError && detail.error.status === 404) {
      return <Alert tone="warn" title="Iniciativa não encontrada">Ela pode ter sido arquivada ou pertencer a outra oficina.</Alert>;
    }
    return <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />;
  }
  return <InitiativeEditor key={`${detail.data.id}:${detail.data.version}`} detail={detail.data} context={detail.data.context} canEdit={detail.data.capabilities.can_edit} />;
}

function InitiativeEditor({ detail, context, canEdit }: {
  detail: InitiativeDetail | null;
  context: InitiativeDetail["context"] | { axis: InitiativeDetail["context"]["axis"]; objective: { id: string; title: string; number: number | null }; key_result: { id: string; title: string; number: number | null } };
  canEdit: boolean;
}) {
  const plan = usePlan();
  const qc = useQueryClient();
  const toast = useToast();
  const [, navigate] = useLocation();
  const [conflict, setConflict] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const units = useQuery({
    queryKey: planKey(plan.id, "units"),
    queryFn: async () => (await get<{ data: Unit[] }>(`/plans/${plan.id}/units`)).data,
  });

  const form = useForm<InitiativeForm>({ resolver: zodResolver(formSchema), defaultValues: toForm(detail), mode: "onSubmit", reValidateMode: "onChange" });
  const fields = useFieldArray({ control: form.control, name: "actions", keyName: "_key" });
  const values = useWatch({ control: form.control });
  const dirty = form.formState.isDirty;
  useUnsavedGuard(canEdit && dirty);

  const unitOptions = useMemo(() => {
    const list = (units.data ?? []).map((u) => ({ id: u.id, name: u.name, archived: false }));
    if (detail?.unit && !list.some((u) => u.id === detail.unit!.id)) list.push({ id: detail.unit.id, name: detail.unit.name ?? "", archived: true });
    return list;
  }, [units.data, detail]);

  const live = useMemo(() => {
    const acts = (values.actions ?? []).map((a) => {
      const p = actionSchema.safeParse(a);
      if (p.success) return p.data;
      // Invalid values never count as filled (an invalid weight cannot be saved).
      const w = a?.weight;
      const validWeight = typeof w === "number" && Number.isInteger(w) && w >= 5 && w <= 100 && w % 5 === 0;
      const reversed = Boolean(a?.starts_on && a?.ends_on && a.ends_on < a.starts_on);
      return { description: a?.description?.trim() || null, starts_on: a?.starts_on || null, ends_on: reversed ? null : a?.ends_on || null, weight: validWeight ? w : null };
    });
    const unitActive = !unitOptions.find((u) => u.id === values.unit_id)?.archived;
    return { readiness: submissionReadiness({ title: values.title ?? "", unit_id: values.unit_id || null, unitActive, actions: acts }), dates: derivedDates(acts) };
  }, [values, unitOptions]);

  // Server field paths (actions.0.weight…) map 1:1 onto form fields.
  const payload = (v: InitiativeForm) => ({
    title: v.title,
    description: v.description,
    unit_id: v.unit_id || null,
    actions: v.actions.map((a, i) => ({ ...a, id: a.id || null, sort_order: i })),
  });

  const handleError = (e: unknown) => {
    if (e instanceof ApiError && e.code === "VERSION_CONFLICT") {
      setConflict(e.message);
      return;
    }
    applyFieldErrors(e, form.setError as never);
    setServerError(e instanceof Error ? e.message : "Não foi possível salvar.");
    // Bring the problem into view: the first invalid field, or the message at the top (UX-14).
    const first = e instanceof ApiError ? e.fieldErrors[0]?.path : undefined;
    if (first) {
      try {
        form.setFocus(first as never, { shouldSelect: false });
      } catch {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = form.handleSubmit(async (v) => {
    setServerError(null);
    try {
      if (!detail) {
        const res = await post<{ data: InitiativeDetail }>(`/plans/${plan.id}/initiatives`, { ...payload(v), key_result_id: context.key_result.id });
        form.reset(v);
        await qc.invalidateQueries({ queryKey: planKey(plan.id, "structure") });
        toast("success", "Rascunho salvo.");
        navigate(`/planos/${plan.id}/iniciativas/${res.data.id}`, { replace: true });
      } else {
        const res = await put<{ data: InitiativeDetail }>(`/plans/${plan.id}/initiatives/${detail.id}`, { ...payload(v), expected_version: detail.version });
        qc.setQueryData(planKey(plan.id, "initiative", detail.id), res.data);
        void qc.invalidateQueries({ queryKey: planKey(plan.id, "structure") });
        toast("success", "Rascunho salvo.");
      }
    } catch (e) {
      handleError(e);
    }
  });

  const reloadCurrent = async () => {
    setConflict(null);
    if (detail) await qc.invalidateQueries({ queryKey: planKey(plan.id, "initiative", detail.id) });
  };

  useEffect(() => {
    if (!canEdit) form.reset(toForm(detail));
  }, [canEdit, detail, form]);

  const readOnly = !canEdit;
  const errors = form.formState.errors;
  const ctx = context;
  const [archiving, setArchiving] = useState(false);
  usePageTitle(detail?.title ?? "Nova iniciativa", plan.name);
  const pendingCount = live.readiness.pending_fields.length;
  return (
    <>
      <PageHeader
        back={<BackLink fallback={`/planos/${plan.id}/planejamento`} label="Voltar" />}
        actions={detail?.capabilities.can_archive && (
          <OverflowMenu label="Mais opções da iniciativa" items={[
            { label: "Arquivar iniciativa", danger: true, icon: <Archive className="size-4" />, onSelect: () => setArchiving(true) },
          ]} />
        )}
        crumb={
          <span className="flex flex-wrap items-center gap-1">
            <GuardedLink href={`/planos/${plan.id}/planejamento`} className="tap text-primary">Planejamento</GuardedLink> /
            <AxisTag name={ctx.axis.name} color={ctx.axis.color} /> / <span>{objectiveLabel(ctx.objective.number)}: {ctx.objective.title}</span> / <span>{krLabel(ctx.key_result.number)}: {ctx.key_result.title}</span>
          </span>
        }
        title={detail ? detail.title : "Nova iniciativa"}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            {detail?.number && <span className="font-semibold text-ink">{initiativeLabel(detail.number)}</span>}
            <StatusPill status={detail?.status ?? "draft"} />
            {detail?.current_revision && <span>Versão submetida {detail.current_revision.revision_number}</span>}
            {detail?.updated_by && <span>Última alteração por {detail.updated_by} em {formatInstantBR(detail.updated_at)}</span>}
          </span>
        }
      />

      {conflict && (
        <Alert tone="warn" title="Conflito de edição" action={<Button variant="secondary" onClick={() => void reloadCurrent()}>Carregar versão atual (descarta suas alterações)</Button>}>
          {conflict} Suas alterações continuam na tela até você recarregar.
        </Alert>
      )}
      {detail?.return_comment && (
        <Alert tone="warn" title="Ajustes solicitados pelo instrutor">
          <p className="whitespace-pre-line">{detail.return_comment.comment}</p>
          <p className="mt-1 text-xs">{detail.return_comment.decided_by} · {formatInstantBR(detail.return_comment.decided_at)}</p>
        </Alert>
      )}
      {detail && !detail.archived_at && (detail.status === "validated" || detail.status === "in_review") && (detail.capabilities.can_request_reopen || plan.capabilities.can_manage) ? (
        <ReopenBanner target={{ entity_type: "initiative", entity_id: detail.id, title: detail.title }} request={detail.reopen_request}
          canAsk={detail.capabilities.can_request_reopen} isInstructor={plan.capabilities.can_manage} lockedMessage={detail.readonly_reason ?? ""} />
      ) : readOnly && (detail?.readonly_reason ?? (!canEdit && `Somente consulta. Edição pelo grupo ${ctx.axis.editor_group_name ?? "do instrutor"}.`)) && (
        <Alert tone="info">{detail?.readonly_reason ?? `Somente consulta. Edição pelo grupo ${ctx.axis.editor_group_name}.`}</Alert>
      )}
      {serverError && !conflict && <Alert tone="danger">{serverError}</Alert>}

      {detail?.status === "validated" && !detail.archived_at && <ProgressPanel initiativeId={detail.id} color={ctx.axis.color} />}

      <form noValidate onSubmit={save}>
        <fieldset disabled={readOnly} className="min-w-0">
          <Panel>
            <div className="grid gap-x-5 md:grid-cols-2">
              <TextField label="Nome da iniciativa" required error={errors.title?.message} {...form.register("title")} />
              <SelectField label="Unidade responsável" error={errors.unit_id?.message} {...form.register("unit_id")}
                hint={units.data && units.data.length === 0 ? "Nenhuma unidade cadastrada. O instrutor cadastra unidades em Configurações." : undefined}>
                <option value="">Selecione…</option>
                {unitOptions.map((u) => <option key={u.id} value={u.id} disabled={u.archived}>{u.name}{u.archived ? " (arquivada)" : ""}</option>)}
              </SelectField>
            </div>
            <TextArea label="Descrição (opcional)" rows={3} error={errors.description?.message} {...form.register("description")} />
          </Panel>

          <Panel className="mt-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-lg font-bold">Ações e entregas</h2>
              <p className="text-xs text-muted">Pelo menos 2 ações · pesos de 5 a 100, em múltiplos de 5, somando 100</p>
            </div>
            <div className="mt-3 hidden grid-cols-[2rem_minmax(0,1fr)_9.5rem_9.5rem_6.5rem_7rem] gap-3 border-b border-line pb-2 text-xs font-bold text-muted md:grid" aria-hidden>
              <span>#</span><span>Ação / entrega</span><span>Início</span><span>Término</span><span>Peso</span><span />
            </div>
            {fields.fields.length === 0 && <p className="py-4 text-sm text-muted">Nenhuma ação cadastrada ainda.</p>}
            <ol className="divide-y divide-line">
              {fields.fields.map((f, i) => {
                const e = errors.actions?.[i];
                return (
                  <li key={f._key} className="grid gap-x-3 gap-y-1 py-3 md:grid-cols-[2rem_minmax(0,1fr)_9.5rem_9.5rem_6.5rem_7rem] md:items-start">
                    <span className="pt-2 text-sm font-bold text-muted">{i + 1}</span>
                    <ActionField label="Ação / entrega" error={e?.description?.message}>
                      {(id) => <textarea id={id} rows={2} aria-invalid={e?.description ? true : undefined}
                        className={cx(inputCls, "autogrow")} {...form.register(`actions.${i}.description`)} />}
                    </ActionField>
                    <ActionField label="Início" error={e?.starts_on?.message}>
                      {(id) => <input id={id} type="date" aria-invalid={e?.starts_on ? true : undefined} className={inputCls} {...form.register(`actions.${i}.starts_on`)} />}
                    </ActionField>
                    <ActionField label="Término" error={e?.ends_on?.message}>
                      {(id) => <input id={id} type="date" aria-invalid={e?.ends_on ? true : undefined} className={inputCls} {...form.register(`actions.${i}.ends_on`)} />}
                    </ActionField>
                    <ActionField label="Peso" error={e?.weight?.message}>
                      {(id) => <input id={id} type="number" inputMode="numeric" min={5} max={100} step={5} aria-invalid={e?.weight ? true : undefined}
                        className={inputCls} {...form.register(`actions.${i}.weight`, { setValueAs: (v) => (v === "" || v === null ? null : Number(v)) })} />}
                    </ActionField>
                    {!readOnly && (
                      <div className="flex gap-1 md:pt-0.5">
                        <IconBtn label={`Mover ação ${i + 1} para cima`} disabled={i === 0} onClick={() => fields.move(i, i - 1)}><ArrowUp className="size-4" /></IconBtn>
                        <IconBtn label={`Mover ação ${i + 1} para baixo`} disabled={i === fields.fields.length - 1} onClick={() => fields.move(i, i + 1)}><ArrowDown className="size-4" /></IconBtn>
                        <IconBtn label={`Remover ação ${i + 1}`} onClick={() => fields.remove(i)}><Trash2 className="size-4" /></IconBtn>
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
            {!readOnly && (
              <Button variant="secondary" className="mt-2" onClick={() => fields.append(blankAction())} disabled={fields.fields.length >= LIMITS.collection}>
                <Plus className="size-4" aria-hidden />Adicionar ação
              </Button>
            )}

            <div className="mt-4 grid gap-3 rounded-ctl bg-paper px-4 py-3 text-sm sm:grid-cols-3">
              <p><strong className={cx("text-lg", live.readiness.weight_total === 100 ? "text-primary" : "text-warn-ink")}>{live.readiness.weight_total} / 100</strong> pontos</p>
              <p>{live.readiness.complete_actions} ação(ões) preenchida(s)</p>
              <p>Período previsto: {live.dates.starts_on ? `${formatDateBR(live.dates.starts_on)} a ${formatDateBR(live.dates.ends_on)}` : "a definir"} <span className="text-muted">· derivado das ações</span></p>
            </div>
            <p className="mt-2 text-xs text-muted">Os pesos distribuem o esforço da iniciativa. Não representam execução.</p>
          </Panel>
        </fieldset>

        {canEdit && pendingCount > 0 && (
          <div id="pendencias" className="scroll-mt-24">
          <Alert tone="info" title="Para submeter, ainda falta:">
            <ul className="list-disc pl-5">
              {[...new Set(live.readiness.pending_fields.map((p) => p.path.startsWith("actions.") ? `Ação ${Number(p.path.split(".")[1]) + 1}: ${p.message}` : p.message))].map((m) => <li key={m}>{m}</li>)}
            </ul>
            <p className="mt-1 text-xs">O rascunho pode ser salvo assim mesmo.</p>
          </Alert>
          </div>
        )}

        {detail && <History detail={detail} />}

        {(canEdit || detail) && (
          <StickyActions
            dirty={canEdit && dirty}
            onSave={canEdit ? () => void save() : undefined}
            summary={<span className={cx("font-semibold", live.readiness.weight_total === 100 ? "text-primary" : "text-warn-ink")}>{live.readiness.weight_total}/100 pontos</span>}
          >
            {canEdit && <Button type="submit" loading={form.formState.isSubmitting} disabled={!!detail && !dirty}>Salvar rascunho</Button>}
            {detail && (
              <InitiativeActions detail={detail} dirty={dirty} readiness={live.readiness} getPayload={() => payload(form.getValues())} onConflict={setConflict} />
            )}
          </StickyActions>
        )}
      </form>

      {archiving && detail && (
        <ArchiveDialog item={{ kind: "initiative", id: detail.id, version: detail.version, title: detail.title }}
          onClose={() => setArchiving(false)} onArchived={() => navigate(`/planos/${plan.id}/planejamento`)} />
      )}
    </>
  );
}

const inputCls = "block w-full rounded-ctl border border-field bg-white px-3 py-2 text-[15px] disabled:bg-locked disabled:text-muted aria-[invalid=true]:border-danger-ink";

/** Visible label on mobile cards; screen-reader label on the desktop grid (headers are visual only). */
function ActionField({ label, error, children }: { label: string; error?: string | undefined; children: (id: string) => ReactNode }) {
  const id = useId();
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="mb-1 block text-xs font-bold md:sr-only">{label}</label>
      {children(id)}
      {error && <p role="alert" className="mt-1 text-xs font-medium text-danger-ink">{error}</p>}
    </div>
  );
}

function IconBtn({ label, onClick, disabled, children }: { label: string; onClick: () => void; disabled?: boolean; children: ReactNode }) {
  return (
    <button type="button" aria-label={label} title={label} onClick={onClick} disabled={disabled}
      className="inline-flex size-9 items-center justify-center rounded-ctl border border-line-strong bg-surface text-ink shadow-e1 transition-[background-color,border-color,transform] duration-[var(--duration-base)] hover:border-ink-3/50 hover:bg-paper active:translate-y-px disabled:opacity-40 disabled:shadow-none">
      {children}
    </button>
  );
}

function History({ detail }: { detail: InitiativeDetail }) {
  const plan = usePlan();
  if (detail.revisions.length === 0 && detail.reopenings.length === 0) return null;
  return (
    <Panel className="mt-6">
      <h2 className="text-lg font-bold">Histórico de revisões</h2>
      <ol className="mt-2 divide-y divide-line">
        {detail.revisions.map((r) => (
          <li key={r.id} className="py-3 text-sm">
            <p className="flex flex-wrap items-center gap-2">
              <GuardedLink href={`/planos/${plan.id}/revisoes/${r.id}`} className="tap font-semibold text-primary">Versão submetida {r.revision_number}</GuardedLink>
              <span className="text-muted">enviada por {r.submitted_by_name} em {formatInstantBR(r.submitted_at)}</span>
            </p>
            {r.decision ? (
              <p className="mt-1">
                {r.decision === "validate" ? "Planejamento validado" : "Ajustes solicitados"} por {r.decided_by_name} em {formatInstantBR(r.decided_at)}
                {r.comment && <span className="mt-0.5 block whitespace-pre-line text-muted">“{r.comment}”</span>}
              </p>
            ) : (
              <p className="mt-1 text-muted">Aguardando decisão do instrutor.</p>
            )}
          </li>
        ))}
        {detail.reopenings.map((o, i) => (
          <li key={`r${i}`} className="py-3 text-sm">
            <p><strong>Reaberta</strong> por {o.actor_name} em {formatInstantBR(o.occurred_at)}</p>
            {o.reason && <p className="text-muted">Justificativa: {o.reason}</p>}
          </li>
        ))}
      </ol>
    </Panel>
  );
}
